import React, { useState, useEffect, useMemo } from 'react';

import { DESCRIPTOR_TYPE_OPTIONS } from '@vlabs/api-bindings/src/constants';
import {
  ChevronDownIcon, ChevronRightIcon, InfoCircleIcon, CheckSquareIcon, EmptySquareIcon, MinusSquareIcon, PlusSquareIcon,
} from '@vlabs/icons';
import SelectOptionGroupLabel from '@vlabs/shared/legacy-components/select-option-group-label/SelectOptionGroupLabel';
import validate from '@vlabs/shared/validators';
import {
  Control, Divider, Tooltip, Tabs, Tab, Step,
} from '@vlabs/uikit';
import cn from 'classnames';
import CheckboxTree from 'react-checkbox-tree';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { OrderSelector } from './OrderSelector';
import st from './StepColumns.module.sass';
import type { TEventsColumns, TFacesColumns } from '../../types';
import { columnsArrayToObject } from '../../utils';

const CheckboxTreeComponent = CheckboxTree.default;

export function StepColumns(): React.JSX.Element {
  const {
    register, control, setValue, clearErrors, setError, formState: { errors },
  } = useFormContext();
  const settings = useWatch({ name: 'columnsSettings' });

  const { t } = useTranslation();
  const [checkedColumns, setCheckedColumns] = useState<TFacesColumns[] | TEventsColumns[]>(settings.defaults);
  const [expandedColumns, setExpandedColumns] = useState<string[]>(['all']);

  useEffect(() => {
    if (checkedColumns.length === 0) {
      setError('content.columns', {
        type: 'custom',
        message: t('features:exportTask.form.errors.chooseColumn') as unknown as string,
      });
      return;
    }
    clearErrors('content.columns');
    setValue('content.columns', columnsArrayToObject(checkedColumns));
  }, [checkedColumns, clearErrors, setValue, setError, t]);

  const icons = useMemo(() => ({
    check: <CheckSquareIcon />,
    uncheck: <EmptySquareIcon />,
    halfCheck: <MinusSquareIcon />,
    expandClose: <ChevronRightIcon />,
    expandOpen: <ChevronDownIcon />,
    expandAll: <PlusSquareIcon />,
    collapseAll: <MinusSquareIcon />,
  }), []);

  return (
    <Step>
      <h6>{t('features:exportTask.form.titles.additionalParameters')}</h6>
      <div
        className={cn(st.Border, {
          [st.Border__error]: errors.content?.columns,
        })}
      >
        <span className={st.ErrorMessage}>
          {errors.content?.columns?.message}
        </span>
        <Tabs data-testid="exportColumns">
          <Tab
            header={t('features:exportTask.form.titles.columns') as unknown as string}
            key="columns"
            name="columns"
          >
            <CheckboxTreeComponent
              checked={checkedColumns}
              expanded={expandedColumns}
              expandOpen
              icons={icons}
              nodes={settings.tree}
              onCheck={(checked) => setCheckedColumns(checked as TFacesColumns[] | TEventsColumns[])}
              onExpand={(expanded) => setExpandedColumns(expanded)}
              showExpandAll
              showNodeIcon={false}
            />
          </Tab>
          <Tab
            header={t('features:exportTask.form.titles.order') as unknown as string}
            key="order"
            name="order"
          >
            <OrderSelector
              keyMap={settings.keyMap}
              list={checkedColumns}
              setList={setCheckedColumns}
            />
          </Tab>
        </Tabs>
      </div>
      <Divider small />
      <div className={st.TooltipWrapper}>
        <Control.Switch
          control={control}
          id="content.save_images"
          label={t('features:exportTask.form.titles.saveFaceImages') as unknown as string}
          name="content.save_images"
        />
        <Tooltip
          className="Tooltip_ui"
          tooltipContent={t('features:exportTask.form.tooltips.saveFaceImages')}
        >
          <InfoCircleIcon className="Tooltip_icon" />
        </Tooltip>
      </div>
      <Divider small />
      <Control.Select
        autoFocus
        control={control}
        formatGroupLabel={SelectOptionGroupLabel}
        id="content.descriptor.type"
        label={t('features:exportTask.form.titles.biometricTemplateType') as unknown as string}
        name="content.descriptor.type"
        options={DESCRIPTOR_TYPE_OPTIONS.raw}
      />
      <Divider small />
      <Control.Input
        id="content.csv_delimiter"
        label={t('features:exportTask.form.titles.delimiterCSV') as unknown as string}
        {...register('content.csv_delimiter', {
          required: validate.required(),
          maxLength: validate.maxLength(1),
        })}
        errors={errors}
      />
      <Divider small />
    </Step>
  );
}

StepColumns.fieldNames = [
  'content.columns',
  'content.csv_delimiter',
];
